import {
  CHILD_PROFILES_CLAIM,
  type ChildProfilesClaim,
  GRANTED_APPLICATION_CLAIM,
  type GrantedApplicationsClaim,
  isChildProfilesClaim,
  isOmniOrganizationClaim,
  isOrganizationClaim,
  isOtherProfilesClaim,
  isProfileClaim,
  isRolesClaim,
  OMNI_ORGANIZATION_CLAIM,
  ORGANIZATION_CLAIM,
  type OrganizationClaim,
  OTHER_PROFILES_CLAIM,
  type OtherProfilesClaim,
  PROFILE_CLAIM,
  type ProfileClaim,
  ROLES_CLAIM,
  type RolesClaim,
  USER_CLAIM,
  type UserClaim,
} from '@bp/pim-auth-constants';
import { useAuth } from 'react-oidc-context';
import { updateClaims } from '../utils/authStore';

const useAuthClaims = () => {
  const auth = useAuth();
  if (!auth.user) {
    throw new Error('useAuthClaims must be used within a component wrapped by AuthProvider');
  }

  const pimAuthClaims = {
    getOrganizationUuid: (): string => {
      const profile = auth.user?.profile[PROFILE_CLAIM] as ProfileClaim;
      if (!profile || !profile.organization) {
        throw new Error('Invalid user profile or missing organization');
      }
      return profile.organization.uuid;
    },
    getAllowedOrganizationUuids: (): string[] => {
      const organization = auth.user?.profile[ORGANIZATION_CLAIM];
      if (!organization || !isOrganizationClaim(organization)) {
        throw new Error('Invalid organization claim');
      }
      return [organization.uuid, ...organization.collaboratesWith.map((o) => o.uuid)];
    },
    getOtherProfiles: (): OtherProfilesClaim => {
      const otherProfiles = auth.user?.profile[OTHER_PROFILES_CLAIM];
      if (!otherProfiles || !isOtherProfilesClaim(otherProfiles)) {
        throw new Error('Invalid other profiles claim');
      }
      return otherProfiles;
    },
    getChildProfiles: (): ChildProfilesClaim => {
      const childProfiles = auth.user?.profile[CHILD_PROFILES_CLAIM];
      if (!childProfiles || !isChildProfilesClaim(childProfiles)) {
        throw new Error('Invalid other profiles claim');
      }
      return childProfiles;
    },
    getOmniOrganization: () => {
      const omniOrganization = auth.user?.profile[OMNI_ORGANIZATION_CLAIM];
      if (!omniOrganization || !isOmniOrganizationClaim(omniOrganization)) {
        throw new Error('Invalid or missing omni-organization');
      }
      return omniOrganization;
    },
    getOrganization: (): OrganizationClaim => {
      const organization = auth.user?.profile[ORGANIZATION_CLAIM];
      if (!organization || !isOrganizationClaim(organization)) {
        throw new Error('Invalid or missing organization');
      }
      return organization;
    },
    getProfile: (): ProfileClaim => {
      const profile = auth.user?.profile[PROFILE_CLAIM];
      if (!profile || !isProfileClaim(profile)) {
        throw new Error('Invalid or missing profile');
      }
      return profile;
    },
    getUser: (): UserClaim => {
      if (!auth || !auth.user) {
        throw new Error('No Auth User found');
      }
      return auth.user?.profile[USER_CLAIM] as UserClaim;
    },
    getLoginEmail: (): string => {
      return auth.user?.profile.email ?? '';
    },
    getRoles: (): RolesClaim => {
      const roles = auth.user?.profile[ROLES_CLAIM];
      if (!roles || !isRolesClaim(roles)) {
        return [];
        //throw new Error('User roles information not available');
      }
      return roles;
    },

    getGrantedApplications: (): GrantedApplicationsClaim => {
      const grantedApplications = auth.user?.profile[GRANTED_APPLICATION_CLAIM];
      if (!grantedApplications) {
        return [];
      }
      return grantedApplications as GrantedApplicationsClaim;
    },
  };
  return { pimAuthClaims: pimAuthClaims, updateClaims: updateClaims };
};

export { useAuthClaims };
